import { useEffect, useState } from "react";
import { formatChinaDateTime } from "../time";

import {
  confirmNoProperty,
  confirmPropertyUpload,
  getPropertyOrderHistory,
  getPropertyUploadPreview,
  listPropertyOrders,
  uploadPropertyWorkbook,
  type PropertyOrder,
  type PropertyOrderHistory,
  type PropertyReport,
  type PropertyUpload,
} from "./api";
import { PropertyResultFrame } from "./PropertyResultFrame";

const PAGE_SIZE = 10;

function pages(total: number): number {
  return Math.max(1, Math.ceil(total / PAGE_SIZE));
}

export function PropertyOrders() {
  const [bucket, setBucket] = useState<"pending" | "completed">("pending");
  const [orders, setOrders] = useState<PropertyOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [draftQuery, setDraftQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeOrder, setActiveOrder] = useState<PropertyOrder | null>(null);
  const [activeMode, setActiveMode] = useState<"view" | "process" | null>(null);
  const [upload, setUpload] = useState<PropertyUpload | null>(null);
  const [preview, setPreview] = useState<PropertyReport | null>(null);
  const [history, setHistory] = useState<PropertyOrderHistory | null>(null);
  const [confirmingEmpty, setConfirmingEmpty] = useState(false);
  const [modalError, setModalError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState("");
  const [selectedVersionNo, setSelectedVersionNo] = useState<number | null>(null);

  async function reload() {
    setLoading(true);
    setError("");
    try {
      const result = await listPropertyOrders({ bucket, query, page, pageSize: PAGE_SIZE });
      setOrders(result.items);
      setTotal(result.total);
      if (page > pages(result.total)) setPage(pages(result.total));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "订单加载失败");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, [bucket, page, query]);
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  async function openOrder(order: PropertyOrder, mode: "view" | "process") {
    setActiveOrder(order);
    setActiveMode(mode);
    setUpload(null);
    setPreview(null);
    setHistory(null);
    setConfirmingEmpty(false);
    setModalError("");
    setSelectedVersionNo(null);
    if (order.status === "COMPLETED") {
      try {
        const loadedHistory = await getPropertyOrderHistory(order.id);
        setHistory(loadedHistory);
        setSelectedVersionNo(loadedHistory.versions.at(-1)?.version_no ?? null);
      } catch (reason) {
        setModalError(reason instanceof Error ? reason.message : "处理记录加载失败");
      }
    }
  }

  async function chooseFile(file: File | undefined) {
    if (!activeOrder || !file) return;
    setSubmitting(true);
    setModalError("");
    setPreview(null);
    try {
      const parsed = await uploadPropertyWorkbook(activeOrder.id, file);
      setUpload(parsed);
      setToast(`解析成功，共识别 ${parsed.property_count} 处不动产`);
    } catch (reason) {
      setUpload(null);
      setModalError(reason instanceof Error ? reason.message : "文件解析失败");
    } finally {
      setSubmitting(false);
    }
  }

  async function showPreview() {
    if (!activeOrder || !upload) return;
    setSubmitting(true);
    setModalError("");
    try {
      setPreview(await getPropertyUploadPreview(activeOrder.id, upload.id));
    } catch (reason) {
      setModalError(reason instanceof Error ? reason.message : "解析结果加载失败");
    } finally {
      setSubmitting(false);
    }
  }

  async function completeUpload() {
    if (!activeOrder || !upload) return;
    setSubmitting(true);
    setModalError("");
    try {
      await confirmPropertyUpload(activeOrder.id, upload.id);
      const wasReprocessing = activeOrder.status === "COMPLETED";
      setActiveOrder(null);
      setActiveMode(null);
      setToast(wasReprocessing ? "订单已重新处理，新结果版本已生成" : "订单已完成");
      await reload();
    } catch (reason) {
      setModalError(reason instanceof Error ? reason.message : "订单完成失败");
    } finally {
      setSubmitting(false);
    }
  }

  async function completeNoProperty() {
    if (!activeOrder) return;
    setSubmitting(true);
    setModalError("");
    try {
      await confirmNoProperty(activeOrder.id, crypto.randomUUID());
      const wasReprocessing = activeOrder.status === "COMPLETED";
      setActiveOrder(null);
      setActiveMode(null);
      setToast(wasReprocessing ? "订单已重新处理，新结果版本已生成" : "已确认名下无房产");
      await reload();
    } catch (reason) {
      setConfirmingEmpty(false);
      setModalError(reason instanceof Error ? reason.message : "确认失败");
    } finally {
      setSubmitting(false);
    }
  }

  function switchBucket(value: "pending" | "completed") {
    setBucket(value);
    setPage(1);
  }

  function closeOrder() {
    setActiveOrder(null);
    setActiveMode(null);
  }

  const selectedVersion = history?.versions.find((version) => version.version_no === selectedVersionNo)
    ?? history?.versions.at(-1);

  return (
    <section className="platform-subaccounts platform-property-orders" aria-label="不动产订单">
      <div className="platform-panel">
        <div className="platform-order-tabs" role="tablist" aria-label="不动产订单状态">
          <button className={bucket === "pending" ? "is-active" : ""} type="button" onClick={() => switchBucket("pending")}>待处理</button>
          <button className={bucket === "completed" ? "is-active" : ""} type="button" onClick={() => switchBucket("completed")}>已完成</button>
        </div>
        <form className="platform-list-toolbar" onSubmit={(event) => { event.preventDefault(); setPage(1); setQuery(draftQuery.trim()); }}>
          <div className="platform-list-search">
            <input aria-label="搜索不动产订单" placeholder="搜索订单号、机构或查询对象" value={draftQuery} onChange={(event) => setDraftQuery(event.target.value)} />
            <button type="submit">查询</button>
            {(query || draftQuery) && <button className="is-secondary" type="button" onClick={() => { setDraftQuery(""); setQuery(""); setPage(1); }}>重置</button>}
          </div>
        </form>
        <div className="platform-table-wrap">
          <table className="platform-account-table platform-property-table">
            <thead><tr><th>订单号</th><th>机构</th><th>订单类型</th><th>查询对象</th><th>身份标识</th><th>提交时间</th><th>操作</th></tr></thead>
            <tbody>
              {!loading && !error && orders.map((order) => <tr key={order.id}>
                <td><strong>{order.order_number}</strong></td><td>{order.institution_name || "—"}</td><td>{order.subject_type === "enterprise" ? "企业" : "个人"}</td><td>{order.subject_name}</td><td><span className="platform-identity-label">{order.subject_identifier_label}</span>{order.subject_identifier}</td><td>{formatChinaDateTime(order.created_at)}</td>
                <td><div className="platform-row-actions">{order.status === "COMPLETED" ? <>
                  <button type="button" onClick={() => void openOrder(order, "view")}>查看结果</button>
                  <button type="button" onClick={() => void openOrder(order, "process")}>重新处理</button>
                </> : <button type="button" onClick={() => void openOrder(order, "process")}>处理</button>}</div></td>
              </tr>)}
              {loading && <tr><td className="platform-table-state" colSpan={7}>正在加载订单…</td></tr>}
              {!loading && error && <tr><td className="platform-table-state is-error" colSpan={7}>{error}<button type="button" onClick={() => void reload()}>重新加载</button></td></tr>}
              {!loading && !error && orders.length === 0 && <tr><td className="platform-table-state" colSpan={7}>{bucket === "pending" ? "暂无待处理不动产订单" : "暂无已完成不动产订单"}</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="platform-pagination"><span>共 {total} 条，每页 {PAGE_SIZE} 条</span><div><button type="button" disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}>上一页</button><span>{page} / {pages(total)}</span><button type="button" disabled={page >= pages(total) || loading} onClick={() => setPage((value) => value + 1)}>下一页</button></div></div>
      </div>

      {activeOrder && activeMode === "process" && <div className="platform-modal-layer" role="presentation">
        <section className="platform-modal platform-property-modal" role="dialog" aria-modal="true" aria-labelledby="property-order-title">
          <div className="platform-modal-heading"><h2 id="property-order-title">{activeOrder.status === "COMPLETED" ? "重新处理不动产订单" : "处理不动产订单"}</h2><button aria-label="关闭" type="button" onClick={closeOrder}>×</button></div>
          <dl className="platform-property-order-summary"><div><dt>订单号</dt><dd>{activeOrder.order_number}</dd></div><div><dt>机构</dt><dd>{activeOrder.institution_name || "—"}</dd></div><div><dt>订单类型</dt><dd>{activeOrder.subject_type === "enterprise" ? "企业" : "个人"}</dd></div><div><dt>{activeOrder.subject_type === "enterprise" ? "企业名称" : "姓名"}</dt><dd>{activeOrder.subject_name}</dd></div><div><dt>{activeOrder.subject_identifier_label}</dt><dd>{activeOrder.subject_identifier}</dd></div></dl>
          {activeOrder.status === "COMPLETED" && <p className="platform-property-reprocess-notice">重新处理将生成新的结果版本，并更新客户当前可见的查询结果，原处理版本仍会保留。</p>}
          <label className="platform-property-upload">上传查询结果 Excel<input type="file" accept=".xls,.xlsx" disabled={submitting} onChange={(event) => void chooseFile(event.target.files?.[0])} /></label>
          {upload && <div className="platform-property-parse-success"><strong>解析成功，共识别 {upload.property_count} 处不动产</strong><button className="platform-inline-action" type="button" onClick={() => void showPreview()}>查看解析结果</button></div>}
          {history && <p className="platform-property-history-summary">历史处理：{history.versions.length} 个结果版本，{history.uploads.length} 次文件上传</p>}
          {modalError && <p className="platform-modal-error">{modalError}</p>}
          <div className="platform-modal-actions">
            <button className="is-secondary" type="button" onClick={closeOrder}>取消</button>
            <button className="is-secondary" type="button" disabled={submitting} onClick={() => setConfirmingEmpty(true)}>{activeOrder.subject_type === "enterprise" ? "确认名下无不动产" : "确认名下无房产"}</button>
            <button className="platform-primary-button" type="button" disabled={submitting || !upload} onClick={() => void completeUpload()}>确认并完成订单</button>
          </div>
        </section>
      </div>}

      {activeOrder && activeMode === "view" && <div className="platform-modal-layer platform-property-preview-layer" role="presentation">
        <section className="platform-modal platform-property-result-modal" role="dialog" aria-modal="true" aria-label="不动产订单结果">
          <div className="platform-modal-heading"><div><h2>查看处理结果</h2><p>{activeOrder.order_number} · {activeOrder.subject_name}</p></div><button aria-label="关闭结果" type="button" onClick={closeOrder}>×</button></div>
          {history && history.versions.length > 0 && <div className="platform-property-version-bar"><span>共 {history.versions.length} 个处理版本</span><label>查看版本<select aria-label="查看处理版本" value={selectedVersion?.version_no ?? ""} onChange={(event) => setSelectedVersionNo(Number(event.target.value))}>{[...history.versions].reverse().map((version) => <option key={version.id} value={version.version_no}>版本 {version.version_no} · {formatChinaDateTime(version.created_at)}</option>)}</select></label></div>}
          {!history && !modalError && <div className="platform-property-result-state">正在加载处理结果…</div>}
          {history && history.versions.length === 0 && <div className="platform-property-result-state">该订单暂无可查看的处理结果</div>}
          {modalError && <div className="platform-property-result-state is-error">{modalError}</div>}
          {selectedVersion && <PropertyResultFrame report={selectedVersion.content} />}
        </section>
      </div>}

      {activeOrder && confirmingEmpty && <div className="platform-modal-layer platform-confirm-layer" role="presentation">
        <section className="platform-modal platform-confirm-modal" role="alertdialog" aria-modal="true" aria-labelledby="no-property-confirm-title">
          <div className="platform-modal-heading"><h2 id="no-property-confirm-title">{activeOrder.subject_type === "enterprise" ? "确认名下无不动产" : "确认名下无房产"}</h2></div>
          <p>{activeOrder.subject_type === "enterprise" ? "确认该企业名下无不动产？" : "确认该查询对象名下无房产？"}确认后订单将直接完成。</p>
          <div className="platform-modal-actions">
            <button className="is-secondary" type="button" disabled={submitting} onClick={() => setConfirmingEmpty(false)}>取消</button>
            <button className="platform-primary-button" type="button" disabled={submitting} onClick={() => void completeNoProperty()}>确认无房产</button>
          </div>
        </section>
      </div>}

      {preview && <div className="platform-modal-layer platform-property-preview-layer" role="presentation"><section className="platform-modal platform-property-preview-modal" role="dialog" aria-modal="true" aria-label="解析结果预览"><div className="platform-modal-heading"><h2>解析结果预览</h2><button aria-label="关闭预览" type="button" onClick={() => setPreview(null)}>×</button></div><PropertyResultFrame report={preview} /></section></div>}
      {toast && <div className="platform-toast" role="status">{toast}</div>}
    </section>
  );
}
